import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getCosts, getEquipmentAreas } from '../services/maintenanceService';
import ScrollHint from '../components/ScrollHint';
import { useLocationContext } from '../contexts/LocationContext';

const MaintenanceCosts = () => {
  const { currentLocation } = useLocationContext();
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [category, setCategory] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);

  const WORK_ORDER_TYPES = [
    { value: 'task', label: 'Task' },
    { value: 'repair', label: 'Repair' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'capex', label: 'Capex' },
  ];

  const toggleType = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  useEffect(() => {
    fetchCategories();
    fetchCosts();
  }, [currentLocation?.id]);

  const fetchCategories = async () => {
    try {
      const data = await getEquipmentAreas();
      const cats = [...new Set(data.equipment_areas.map(a => a.category))];
      setCategories(cats);
    } catch (err) {
      // Non-critical
    }
  };

  const fetchCosts = async (params = {}) => {
    try {
      setLoading(true);
      const data = await getCosts(params);
      setCosts(data);
    } catch (err) {
      setError('Failed to load cost data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (category) params.category = category;
    if (selectedTypes.length > 0) params.type = selectedTypes.join(',');
    fetchCosts(params);
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setCategory('');
    setSelectedTypes([]);
    fetchCosts();
  };

  const exportToExcel = () => {
    if (!costs) return;

    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryRows = [
      { 'Metric': 'Total Spent', 'Value': `$${costs.summary.total_spent.toFixed(2)}` },
      { 'Metric': 'Total Work Orders', 'Value': costs.summary.total_work_orders },
      { 'Metric': 'Average Cost', 'Value': `$${costs.summary.avg_cost.toFixed(2)}` },
      { 'Metric': 'Most Expensive Area', 'Value': costs.summary.most_expensive_area || 'N/A' }
    ];
    if (startDate || endDate) {
      summaryRows.push({ 'Metric': 'Date Range', 'Value': `${startDate || 'Start'} to ${endDate || 'Present'}` });
    }
    if (category) {
      summaryRows.push({ 'Metric': 'Category Filter', 'Value': category });
    }
    if (selectedTypes.length > 0) {
      summaryRows.push({ 'Metric': 'Type Filter', 'Value': selectedTypes.join(', ') });
    }
    const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
    summaryWs['!cols'] = [{ wch: 25 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // By Area sheet
    const areaRows = costs.by_area.map(row => ({
      'Area': row.area,
      'Work Orders': row.count,
      'Parts Cost': row.parts_cost,
      'Labor Cost': row.labor_cost,
      'Total Cost': row.total_cost
    }));
    const areaWs = XLSX.utils.json_to_sheet(areaRows);
    areaWs['!cols'] = [{ wch: 25 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, areaWs, 'By Area');

    // By Month sheet
    const monthRows = costs.by_month.map(row => ({
      'Month': row.month,
      'Work Orders': row.count,
      'Parts Cost': row.parts_cost,
      'Labor Cost': row.labor_cost,
      'Total Cost': row.total_cost
    }));
    const monthWs = XLSX.utils.json_to_sheet(monthRows);
    monthWs['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, monthWs, 'By Month');

    // Work Orders sheet
    if (costs.work_orders && costs.work_orders.length > 0) {
      const woRows = costs.work_orders.map(wo => ({
        'Date': wo.completed_date || '',
        'Title': wo.title,
        'Type': wo.type,
        'Area': wo.area,
        'Parts Cost': wo.parts_cost,
        'Labor Cost': wo.labor_cost,
        'Total Cost': wo.total_cost,
        'Vendor': wo.vendor || '',
      }));
      const woWs = XLSX.utils.json_to_sheet(woRows);
      woWs['!cols'] = [{ wch: 12 }, { wch: 35 }, { wch: 12 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, woWs, 'Work Orders');
    }

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    const dateStr = new Date().toISOString().split('T')[0];
    saveAs(blob, `Maintenance_Costs_${dateStr}.xlsx`);
  };

  if (loading && !costs) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Maintenance Costs</h1>
        <div className="flex space-x-3">
          <button
            onClick={exportToExcel}
            disabled={!costs || costs.summary.total_work_orders === 0}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            Export to Excel
          </button>
          <Link
            to="/maintenance"
            className="text-gray-600 hover:text-gray-800 text-sm font-medium px-4 py-2"
          >
            Back to Maintenance
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <div className="flex gap-1">
              {WORK_ORDER_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => toggleType(t.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    selectedTypes.includes(t.value)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleFilter}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Apply
          </button>
          {(startDate || endDate || category || selectedTypes.length > 0) && (
            <button
              onClick={handleClear}
              className="text-gray-600 hover:text-gray-800 px-4 py-2 text-sm font-medium"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {costs && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Spent</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">${costs.summary.total_spent.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Work Orders</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{costs.summary.total_work_orders}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Average Cost</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">${costs.summary.avg_cost.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Most Expensive Area</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{costs.summary.most_expensive_area || 'N/A'}</p>
            </div>
          </div>

          {/* Spending by Area */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Spending by Area</h2>
            </div>
            {costs.by_area.length > 0 ? (
              <ScrollHint>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Orders</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parts</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {costs.by_area.map(row => (
                      <tr key={row.area} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.area}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{row.count}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">${row.parts_cost.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">${row.labor_cost.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${row.total_cost.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-bold">
                      <td className="px-6 py-4 text-sm text-gray-900">Total</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {costs.by_area.reduce((s, r) => s + r.count, 0)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        ${costs.by_area.reduce((s, r) => s + r.parts_cost, 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        ${costs.by_area.reduce((s, r) => s + r.labor_cost, 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        ${costs.by_area.reduce((s, r) => s + r.total_cost, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </ScrollHint>
            ) : (
              <div className="p-6 text-center text-gray-500">No completed work orders in this period.</div>
            )}
          </div>

          {/* Spending by Month */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Spending by Month</h2>
            </div>
            {costs.by_month.length > 0 ? (
              <ScrollHint>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Orders</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parts</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {costs.by_month.map(row => (
                      <tr key={row.month} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.month}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{row.count}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">${row.parts_cost.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">${row.labor_cost.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${row.total_cost.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollHint>
            ) : (
              <div className="p-6 text-center text-gray-500">No completed work orders in this period.</div>
            )}
          </div>

          {/* Completed Work Orders Detail */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Completed Work Orders
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({costs.work_orders ? costs.work_orders.length : 0})
                </span>
              </h2>
            </div>
            {costs.work_orders && costs.work_orders.length > 0 ? (
              <ScrollHint>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parts</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {costs.work_orders.map(wo => (
                      <tr key={wo.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {wo.completed_date ? new Date(wo.completed_date + 'T12:00:00').toLocaleDateString() : ''}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <Link to={`/maintenance/work-orders/${wo.id}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                            {wo.title}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">{wo.type}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{wo.area}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">${wo.parts_cost.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">${wo.labor_cost.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${wo.total_cost.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-bold">
                      <td className="px-6 py-4 text-sm text-gray-900" colSpan={4}>Total</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        ${costs.work_orders.reduce((s, wo) => s + wo.parts_cost, 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        ${costs.work_orders.reduce((s, wo) => s + wo.labor_cost, 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        ${costs.work_orders.reduce((s, wo) => s + wo.total_cost, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </ScrollHint>
            ) : (
              <div className="p-6 text-center text-gray-500">No completed work orders in this period.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MaintenanceCosts;
